import React from "react";
import { Card, CardLabel } from "./ui/Card";
import { Hatch } from "./ui/Hatch";
import { channelUsesEmail, channelUsesPush } from "./broadcastEnums";
import { resolveDeepLink } from "./broadcastDraft";
import { emailBodyPlainText, sanitizeEmailHtml } from "./emailBodyHtml";

/**
 * The composer's right rail: what the two channels will actually look like.
 *
 * ---------------------------------------------------------------------------
 * IT NEVER FETCHES — DELIBERATELY
 * ---------------------------------------------------------------------------
 * Every pixel here is derived from the draft the author is typing plus one
 * already-resolved `imageUrl` prop. There is no request, no service import and
 * no effect. That is not an accident of the current implementation, it is the
 * property worth preserving: the preview is the only feedback an author gets
 * while composing, and it has to keep working when the estimate endpoint is
 * down, when the media-url call is slow, or when the whole backend is
 * unreachable. A preview that goes blank because a server went away is a
 * preview you cannot trust the rest of the time either.
 *
 * The image is the one thing this rail cannot derive, because the upload URL is
 * private and a direct GET is refused. So the resolved, displayable URL arrives
 * as a prop from the owner — and its absence degrades to the hatch placeholder
 * rather than to an empty box.
 *
 * ---------------------------------------------------------------------------
 * THE ONE SHADOW IN THE CONSOLE
 * ---------------------------------------------------------------------------
 * Broadcasts is otherwise shadow-free: depth comes from hairlines, the warm
 * page tint and the accent. The push notification card is the single exception,
 * and it earns it — a real push sits *on top of* the lock screen, and the
 * hairline lift is what says "this is a system surface, not a panel in your
 * console". If a second shadow ever appears in this directory, one of the two
 * is wrong.
 */

/**
 * Placeholder copy for an empty draft.
 *
 * Rendered in `text-bc-faintest` so it reads as a hole rather than as content.
 * It is shown ONLY here — nothing writes these strings back into the draft, so
 * an author who never types a title still fails validation instead of silently
 * shipping our example copy to 25,000 people.
 */
const TITLE_PLACEHOLDER = "New workouts are live";
const BODY_PLACEHOLDER =
  "Three new strength sessions dropped this week — take a look.";

/**
 * The email's own placeholders. They match the email card's, not the popup's,
 * for the same reason the two cards look different: an author glancing at the
 * rail should be able to tell which surface they are previewing.
 */
const SUBJECT_PLACEHOLDER = "Your March plan is ready";
const EMAIL_BODY_PLACEHOLDER = "Write the email…";

/**
 * Draft text, or its placeholder in the faintest tier.
 *
 * @param {Object} props
 * @param {string} props.value - The author's text. Empty means "not written yet".
 * @param {string} props.placeholder - Shown, muted, when `value` is empty.
 * @param {string} [props.className] - Typography classes from the caller.
 * @returns {React.ReactElement}
 */
const PreviewText = ({ value, placeholder, className = "" }) => (
  <div className={`${className} ${value ? "" : "text-bc-faintest"}`}>
    {value || placeholder}
  </div>
);

/**
 * The image slot: the real image when we have a displayable URL, the hatch when
 * we do not.
 *
 * `alt=""` because the image is decorative *in the preview* — the author is
 * looking at it, and announcing an unnamed image to a screen reader adds
 * nothing the surrounding title and body do not already say.
 *
 * @param {Object} props
 * @param {string} [props.imageUrl] - Resolved, displayable URL, or empty.
 * @param {string} props.heightClass - e.g. "h-16". Applies to both branches.
 * @returns {React.ReactElement}
 */
const PreviewImage = ({ imageUrl, heightClass }) =>
  imageUrl ? (
    <img
      src={imageUrl}
      alt=""
      className={`w-full rounded-md object-cover ${heightClass}`}
    />
  ) : (
    <Hatch className={`w-full ${heightClass}`} />
  );

/**
 * The push notification, as it lands on a phone.
 *
 * @param {Object} props
 * @param {string} props.title
 * @param {string} props.body
 * @param {string} [props.imageUrl]
 * @param {string} props.deepLink - Display-only; the payload sends the route id.
 * @returns {React.ReactElement}
 */
const PushPreview = ({ title, body, imageUrl, deepLink }) => (
  <section aria-label="Popup preview">
    <CardLabel>Popup preview</CardLabel>

    {/* The grey plate stands in for the lock screen the card sits on. */}
    <div className="mt-2 rounded-[10px] bg-[#EFEEEA] p-3">
      <div className="rounded-[10px] bg-white p-3 shadow-[0_1px_2px_rgba(0,0,0,0.06)]">
        <div className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="h-4 w-4 shrink-0 rounded-[4px] bg-orange-primary"
          />
          <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-bc-faint">
            FLEX
          </span>
          <span className="ml-auto text-[11px] text-bc-faintest">now</span>
        </div>

        <PreviewText
          value={title}
          placeholder={TITLE_PLACEHOLDER}
          className="mt-2 text-[13px] font-semibold"
        />
        <PreviewText
          value={body}
          placeholder={BODY_PLACEHOLDER}
          className="mt-1 text-xs text-bc-text-2"
        />

        <div className="mt-2">
          <PreviewImage imageUrl={imageUrl} heightClass="h-16" />
        </div>
      </div>
    </div>

    <div className="mt-1.5 font-mono text-xs text-bc-faint">{deepLink}</div>
  </section>
);

/**
 * The email, as it lands in an inbox.
 *
 * Every value is the author's own. There are no fallbacks left to resolve: the
 * email is composed on its own now, so an empty subject is an empty subject —
 * it shows the muted placeholder and validation blocks the send — rather than
 * quietly displaying a popup title that will not be sent.
 *
 * @param {Object} props
 * @param {string} props.subject - `emailSubject`.
 * @param {string} props.bodyHtml - `emailBodyHtml`, rendered as sanitized markup.
 * @param {string} props.ctaLabel - `emailCtaText`, or the default label.
 * @returns {React.ReactElement}
 */
const EmailPreview = ({ subject, bodyHtml, ctaLabel }) => (
  <section aria-label="Email preview">
    <CardLabel>Email preview</CardLabel>

    <Card className="mt-2 p-3">
      <div className="font-mono text-[11px] text-bc-faint">
        Flex &lt;noreply.flexcellence.co&gt;
      </div>

      <PreviewText
        value={subject}
        placeholder={SUBJECT_PLACEHOLDER}
        className="mt-1.5 text-[13px] font-semibold"
      />

      {/* THE BODY IS RENDERED AS SANITIZED HTML.
          This used to render as raw text, on the reasoning that author-supplied
          markup inside the console is a stored XSS vector. The vector is real;
          text was the wrong mitigation. The body is written in a link editor
          now, and a preview that shows `<a href="…">` as literal tags shows the
          author nothing they can check — which is the failure the whole rail
          exists to prevent.

          What closes the hole instead is `sanitizeEmailHtml`: a nine-tag
          allowlist, `href` and `style` as the only attributes, and an http/
          https/mailto URI regexp, so no event handler and no `javascript:`
          survives. The same function runs on the payload, so what renders here
          is exactly what ships. */}
      {/* NOT `whitespace-pre-wrap`. This is a picture of an inbox, and an inbox
          renders `white-space: normal` — a preview that honours newline
          characters the mail client will collapse is a preview that lies about
          the one thing it exists to show. Line breaks reach here as `<br>`,
          which `sanitizeEmailHtml` guarantees. */}
      <div
        data-testid="bc-email-preview-body"
        className="mt-2 break-words text-xs text-bc-text-2"
      >
        {bodyHtml ? (
          <span dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(bodyHtml) }} />
        ) : (
          <span className="text-bc-faintest">{EMAIL_BODY_PLACEHOLDER}</span>
        )}
      </div>

      {/* Not a <button>: this is a picture of a button. Making it focusable
          would put a dead tab stop in the middle of the composer. */}
      <div className="mt-2.5 inline-flex items-center rounded-lg bg-orange-primary px-3 py-1.5 text-[13px] font-medium text-white">
        {ctaLabel}
      </div>

      <div className="mt-2.5 font-mono text-[11px] text-bc-faintest">
        Unsubscribe · one-click
      </div>
    </Card>
  </section>
);

/** The button label an email shows before the author names it. */
const CTA_PLACEHOLDER = "Open Flex";

/**
 * The sticky preview rail beside the composer.
 *
 * One panel, matching the mode being composed. It used to render both when the
 * draft targeted both channels; a draft targets one now, and showing a preview
 * of a message that is not being written is a preview of nothing.
 *
 * @param {Object} props
 * @param {import("./broadcastDraft").BroadcastDraft} props.draft - The draft being composed.
 * @param {string} [props.imageUrl] - Resolved, displayable image URL for the POPUP. Empty renders the hatch. Ignored on an email, which has no image.
 * @param {string} [props.className] - Extra classes on the rail, merged last.
 * @returns {React.ReactElement}
 */
export const ComposerPreviewRail = ({ draft, imageUrl = "", className = "" }) => {
  const showsPush = channelUsesPush(draft?.channels);
  const showsEmail = channelUsesEmail(draft?.channels);

  const title = String(draft?.title || "").trim();
  const body = String(draft?.body || "").trim();
  const deepLink = resolveDeepLink(draft);

  const emailSubject = String(draft?.emailSubject || "").trim();
  // "Has the author written anything" is a question about the words, not the
  // markup — an untouched-then-cleared editor leaves `<div><br></div>` behind,
  // and rendering that as a body would replace the placeholder with a blank.
  const emailBody = emailBodyPlainText(draft?.emailBodyHtml)
    ? String(draft?.emailBodyHtml || "")
    : "";
  const ctaLabel = String(draft?.emailCtaText || "").trim() || CTA_PLACEHOLDER;

  return (
    <aside className={`sticky top-0 flex flex-col gap-3.5 ${className}`}>
      {showsPush && (
        <PushPreview
          title={title}
          body={body}
          imageUrl={imageUrl}
          deepLink={deepLink}
        />
      )}

      {showsEmail && (
        <EmailPreview
          subject={emailSubject}
          bodyHtml={emailBody}
          ctaLabel={ctaLabel}
        />
      )}

      {/* Unreachable from a valid draft — the mode switch offers exactly two
          values and each lights a panel. Defended anyway, because a blank rail
          looks like the preview crashed. */}
      {!showsPush && !showsEmail && (
        <p className="text-xs text-bc-soft">Pick a channel to see a preview.</p>
      )}
    </aside>
  );
};

export default ComposerPreviewRail;
